
// Components
import JumpcloudGoButton from 'client/components/JumpcloudGoButton.vue';
import JumpcloudGoPageLoader from 'client/components/JumpcloudGoPageLoader.vue';
import SSOGoContinue from 'client/containers/user/login/SSOGoContinue.vue';

// Libraries
import { defineComponent } from 'vue';

// Constants
import {
  accessResponse, durtApiResponseType, extensionTypes, infoResponse,
} from 'client/services/durtConstants';
import LoginConsts from 'client/util/Constants/LoginConstants';
import MfaConstants from 'client/containers/Login/MfaConstants';

// Store
import { mapActions, mapGetters } from 'vuex';

// Services
import durtService from 'client/services/durtService';
import routerService from 'client/services/routerService';

// Utils
import { useLoginLabels } from 'client/util/Constants/LoginLabels';
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import Util from 'client/util/Util';

const { userConsoleRootRoute } = LoginConsts;
const { mfaNames } = MfaConstants;

export default defineComponent({
  name: 'JumpcloudGoHandler',

  components: {
    JumpcloudGoButton,
    JumpcloudGoPageLoader,
    SSOGoContinue,
  },

  data() {
    return {
      controllingQueryParams: {} as Record<string, string>,
      durtInfo: {} as infoResponse,
      durtAccess: {} as accessResponse,
      durtExtType: '',
      shouldShowLoader: false,
      isButtonLoading: true,
      shouldShowContinueButton: false,
    };
  },

  props: {
    mfaType: {
      type: String,
      required: true,
    },
    /** When true, JCGO auto-triggers when extension is ready; when false, user must click */
    isOnlyFactor: {
      type: Boolean,
      default: true,
    },
  },

  setup() {
    const uiLabel = useLoginLabels();
    const mfaLabels = useMfaLabels();

    return {
      uiLabel,
      mfaLabels,
    };
  },

  computed: {
    ...mapGetters('StepUpModel', [
      'stateId',
    ]),

    headingText() {
      return this.mfaLabels.verifyIdentity;
    },

    isUvRequired() {
      return this.mfaType === mfaNames.durt_uv || !!this.durtInfo.is_locked;
    },

    userEmail(): string {
      return this.durtInfo.email || '';
    },
  },

  created() {
    this.initDurt();
  },

  methods: {
    ...mapActions('StepUpModel', [
      'setStateId',
    ]),

    async durtSignin() {
      this.$emit('jcGoStarted');
      try {
        const { isUvRequired, stateId } = this as any;
        this.setButtonState(true);
        this.setLoaderState(!isUvRequired);
        // start a timer to make sure we see the loader.
        const visualDelay = Util.delay(2000);

        try {
          this.setLoaderState(true);
          this.durtAccess = await durtService.getAccess(isUvRequired, stateId);
        } catch (err) {
          this.handleError();
        }

        // show continue button to force extra click for android users
        if (this.durtExtType === extensionTypes.androidHttp
          || this.durtExtType === extensionTypes.android
        ) {
          // accessData.session is a JWT token
          const dust = JSON.parse(atob(this.durtAccess.session.split('.')[1]));
          this.durtInfo = {
            email: dust?.user?.email,
            result: this.durtAccess.result,
          };

          this.setButtonState(false);
          this.setLoaderState(false);
          this.shouldShowContinueButton = true;
        } else {
          await Promise.all([durtService.signin(this.durtAccess), visualDelay]);
          this.$emit('success');
        }
      } catch {
        window.location.replace(`${userConsoleRootRoute}#/?error=goError`);
      }
    },

    async handleError() {
      try {
        await durtService.signout();
      } catch {
        // expect to fail with 401 Unauthorized
        (this as any).setStateId('');
        routerService.replaceToUserLogin();
      }
    },

    async initDurt() {
      if (await durtService.hasExtension()) {
        try {
          this.durtExtType = await durtService.getExtensionType();
          this.durtInfo = await durtService.getInfo();
        } catch (e: any) {
          if (e.message === durtApiResponseType.userNotActive) {
            this.setButtonState(false);
            this.durtInfo.result = e.message;
          } else {
            this.handleError();
          }
          return;
        }
      }

      if (this.durtInfo.result === durtApiResponseType.success) {
        if (this.isOnlyFactor) {
          this.durtSignin();
        } else {
          this.setButtonState(false);
        }
      } else {
        this.handleError();
      }
    },

    async handleSignin() {
      try {
        this.setButtonState(true);
        await durtService.signin(this.durtAccess);

        this.$emit('success');
      } catch {
        window.location.replace(`${userConsoleRootRoute}#/?error=goError`);
      }
    },

    setLoaderState(value: boolean) {
      this.shouldShowLoader = value;
    },

    setButtonState(value: boolean) {
      this.isButtonLoading = value;
    },
  },
});
